import { AlertTriangle } from "lucide-react";
import type { ReportChart, ReportCoverage } from "../api";

/* Formato compartido por el reporte y la biblioteca de gráficos. */

export const money = new Intl.NumberFormat("es-CL", {
  style: "currency", currency: "CLP", maximumFractionDigits: 0,
});
export const integer = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 });
export const decimal = new Intl.NumberFormat("es-CL", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

export function num(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function fmtMoney(value: unknown): string {
  return value === null || value === undefined ? "—" : money.format(num(value));
}

export function fmtInt(value: unknown): string {
  return value === null || value === undefined ? "—" : integer.format(num(value));
}

export function fmtPct(value: unknown, digits = 2): string {
  return value === null || value === undefined ? "—" : `${num(value).toFixed(digits)} %`;
}

export function fmtRatio(value: unknown): string {
  return value === null || value === undefined ? "—" : `${decimal.format(num(value))}x`;
}

export function fmtByKind(value: number, kind: string): string {
  if (kind === "money") return money.format(value);
  if (kind === "percent") return `${value.toFixed(2)} %`;
  if (kind === "ratio") return `${decimal.format(value)}x`;
  return integer.format(value);
}

/** El estado de la fuente viaja pegado al dato, nunca en una nota al pie. */
export function CoverageBadge({ coverage }: { coverage: ReportCoverage }) {
  const label = coverage.state === "REAL" ? "Dato real"
    : coverage.state === "PARCIAL" ? "Dato parcial" : "Sin datos";
  return (
    <span className={`rep-badge is-${coverage.state.toLowerCase()}`} title={coverage.reason ?? undefined}>
      {label}
    </span>
  );
}

export function CoverageNote({ coverage }: { coverage: ReportCoverage }) {
  if (!coverage.reason) return null;
  return (
    <p className={`rep-note is-${coverage.state.toLowerCase()}`}>
      <AlertTriangle size={13} aria-hidden="true" />
      <span>{coverage.reason}</span>
    </p>
  );
}

export function EmptyPanel({ title, reason }: { title: string; reason: string }) {
  return (
    <article className="rep-panel rep-panel-empty">
      <h3>{title}</h3>
      <p>{reason}</p>
    </article>
  );
}

/**
 * Gráfico combinado barras + líneas. Cada serie se escala a su propio máximo para
 * que magnitudes distintas (inversión vs. clics) sigan siendo legibles juntas.
 */
export function Chart({
  chart,
  actions,
  compact,
}: {
  chart: ReportChart;
  actions?: React.ReactNode;
  compact?: boolean;
}) {
  const width = 900;
  const height = compact ? 250 : 300;
  const padX = 46;
  const padTop = 22;
  const plotH = height - padTop - 54;
  const count = chart.labels.length;
  if (!count) {
    return <EmptyPanel title={chart.title} reason="La consulta no devolvió filas para esta ventana." />;
  }

  const step = (width - padX * 2) / count;
  const barSeries = chart.chartType === "line" || chart.chartType === "area" ? [] : chart.series.slice(0, 1);
  const lineSeries = chart.chartType === "bar" ? [] : chart.series.slice(barSeries.length);
  const maxOf = (values: number[]) => Math.max(1, ...values.map((value) => Math.abs(value)));
  const tickEvery = Math.max(1, Math.ceil(count / 12));

  return (
    <article className="rep-panel rep-chart-panel">
      <div className="rep-panel-head">
        <h3>{chart.title}</h3>
        <div className="rep-panel-actions">
          <CoverageBadge coverage={chart.coverage} />
          {actions}
        </div>
      </div>
      <div className="rep-legend">
        {chart.series.map((serie, index) => (
          <span key={serie.id} className={index < barSeries.length ? "is-bar" : "is-line"}>
            {serie.label} · {fmtByKind(serie.values.reduce((sum, value) => sum + value, 0), serie.format)}
          </span>
        ))}
      </div>
      <div className="rep-chart-scroll">
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={chart.title}>
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
            <line key={ratio} className="rep-grid" x1={padX} x2={width - padX}
              y1={padTop + plotH * ratio} y2={padTop + plotH * ratio} />
          ))}
          {barSeries.map((serie) => {
            const max = maxOf(serie.values);
            return serie.values.map((value, index) => {
              const barHeight = (Math.abs(value) / max) * plotH;
              return (
                <rect key={`${serie.id}-${index}`} className="rep-bar"
                  x={padX + step * index + step * 0.18} y={padTop + plotH - barHeight}
                  width={Math.max(2, step * 0.64)} height={Math.max(0, barHeight)} rx="2">
                  <title>{`${chart.labels[index]}: ${fmtByKind(value, serie.format)}`}</title>
                </rect>
              );
            });
          })}
          {lineSeries.map((serie, serieIndex) => {
            const max = maxOf(serie.values);
            const points = serie.values
              .map((value, index) =>
                `${padX + step * index + step / 2},${padTop + plotH - (Math.abs(value) / max) * plotH}`)
              .join(" ");
            return (
              <g key={serie.id}>
                <polyline className={`rep-line is-${serieIndex}`} points={points} />
                {serie.values.map((value, index) => (
                  <circle key={index} className={`rep-dot is-${serieIndex}`} r="2.6"
                    cx={padX + step * index + step / 2}
                    cy={padTop + plotH - (Math.abs(value) / max) * plotH}>
                    <title>{`${chart.labels[index]}: ${fmtByKind(value, serie.format)}`}</title>
                  </circle>
                ))}
              </g>
            );
          })}
          {chart.labels.map((label, index) =>
            index % tickEvery === 0 ? (
              <text key={label + index} x={padX + step * index + step / 2} y={height - 26} textAnchor="middle">
                {label.length > 7 ? label.slice(5) : label}
              </text>
            ) : null,
          )}
        </svg>
      </div>
    </article>
  );
}
